import React from 'react'
import { useDrag } from 'react-dnd'
import { User, Clock, X } from 'lucide-react'
import { useThemeTokens } from '../../themes'
import type { Worker } from '../../types'
import { calculateHoursWorked } from '../../lib/timesheet-business-rules.js'

interface WorkerCardProps {
  worker: Worker
  draggable?: boolean
  showTimeInputs?: boolean
  timeIn?: string
  timeOut?: string
  onRemove?: (workerId: number) => void
  onTimeChange?: (workerId: number, timeIn: string, timeOut: string) => void
  className?: string
}

// Custom hook for responsive grid (following OrderCard pattern)
const useResponsiveGrid = () => {
  const [isMobile, setIsMobile] = React.useState(false)
  
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  return isMobile
}

export const WorkerCard: React.FC<WorkerCardProps> = ({
  worker,
  draggable = false,
  showTimeInputs = false,
  timeIn = '05:00',
  timeOut = '17:00',
  onRemove,
  onTimeChange,
  className
}) => {
  const theme = useThemeTokens()
  const isMobile = useResponsiveGrid()
  const cardRef = React.useRef<HTMLDivElement>(null)
  
  // React DnD setup
  const [{ isDragging }, drag] = useDrag({
    type: 'worker',
    item: { id: worker.id, name: worker.name, worker },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: draggable
  })

  // Attach drag to ref
  React.useEffect(() => {
    if (draggable && cardRef.current) {
      drag(cardRef.current)
    }
  }, [drag, draggable])

  // Calculate hours worked using centralized contract function (includes lunch deduction)
  const hoursWorked = calculateHoursWorked(timeIn, timeOut)

  // Calculate days since last seen
  const daysSinceLastSeen = worker.lastSeen 
    ? Math.floor((Date.now() - new Date(worker.lastSeen).getTime()) / (1000 * 60 * 60 * 24))
    : null

  // Card styles following OrderCard pattern
  const cardStyles: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    padding: theme.spacing.md,
    boxShadow: theme.shadows.sm,
    transition: 'all 0.2s ease',
    cursor: draggable ? 'move' : 'default',
    opacity: isDragging ? 0.5 : 1,
    marginBottom: theme.spacing.sm,
    position: 'relative'
  }

  // Header styles
  const headerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.sm
  }

  // Worker info styles
  const workerInfoStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm
  }

  // Content grid for time inputs
  const contentGridStyles: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
    gap: theme.spacing.sm,
    alignItems: 'center'
  }

  // Time input styles
  const timeInputStyles: React.CSSProperties = {
    padding: theme.spacing.sm,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: theme.typography.fontSize.sm,
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.colors.background,
    color: theme.colors.text
  }

  // Status styles
  const statusStyles: React.CSSProperties = {
    fontSize: theme.typography.fontSize.sm,
    color: worker.status === 'active' ? theme.colors.success : theme.colors.textSecondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm
  }

  // Handle time input changes
  const handleTimeInChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTimeIn = e.target.value
    onTimeChange?.(worker.id, newTimeIn, timeOut)
  }

  const handleTimeOutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTimeOut = e.target.value
    onTimeChange?.(worker.id, timeIn, newTimeOut)
  }

  return (
    <div 
      ref={cardRef}
      style={cardStyles}
      className={className}
      data-testid="worker-card"
    >
      {/* Header */}
      <div style={headerStyles}>
        <div style={workerInfoStyles}>
          <User 
            size={16} 
            color={worker.status === 'active' ? theme.colors.primary : theme.colors.textSecondary} 
          />
          <div>
            <div style={{
              fontSize: theme.typography.fontSize.md,
              fontWeight: theme.typography.fontWeight.medium,
              color: theme.colors.text,
              fontFamily: theme.typography.fontFamily
            }}>
              {worker.name}
            </div>
            <div style={statusStyles}>
              <span>{worker.totalDaysWorked} days worked</span>
              {daysSinceLastSeen !== null && (
                <span>• Last seen {daysSinceLastSeen} days ago</span>
              )}
            </div>
          </div>
        </div>
        
        {onRemove && (
          <button
            onClick={() => onRemove(worker.id)}
            style={{
              background: 'none',
              border: 'none',
              padding: theme.spacing.sm,
              cursor: 'pointer',
              color: theme.colors.error,
              borderRadius: theme.borderRadius.sm,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Remove worker"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Time inputs if enabled */}
      {showTimeInputs && (
        <div style={contentGridStyles}>
          <div>
            <label style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textSecondary,
              display: 'block',
              marginBottom: theme.spacing.sm
            }}>
              Time In
            </label>
            <input
              type="time"
              value={timeIn}
              onChange={handleTimeInChange}
              style={timeInputStyles}
            />
          </div>
          
          <div>
            <label style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textSecondary,
              display: 'block',
              marginBottom: theme.spacing.sm
            }}>
              Time Out
            </label>
            <input
              type="time"
              value={timeOut}
              onChange={handleTimeOutChange}
              style={timeInputStyles}
            />
          </div>
          
          <div>
            <label style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.textSecondary,
              display: 'block',
              marginBottom: theme.spacing.sm
            }}>
              Hours
            </label>
            <div style={{
              padding: theme.spacing.sm,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.text,
              fontWeight: theme.typography.fontWeight.medium,
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.sm
            }}>
              <Clock size={14} />
              {hoursWorked.toFixed(1)}h
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 